import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { decodeJwtPayload } from '../lib/decodeJwtPayload';
import { apiFetch } from '../lib/api';
import { isSafeRedirect } from '../lib/redirectGuard';

// #2282 — the CLIENT-only routing LoginPage.tsx's handleSubmit already does
// (decode → missing tenantId → /client/my-projects → route) only ran once,
// at login. A CLIENT-only account that reloads (token already in
// localStorage, handleSubmit never re-runs) fell through to whatever route
// it reloaded on — silently, since every tenant-scoped endpoint that route
// calls is independently built fail-closed to a benign empty response, not
// an error. Confirmed live on staging (recon #2282). Replicates the same
// decision here so it also runs on reload, not only at fresh login.
//
// These are where that check itself lands a CLIENT-only (or
// project_members-only, #2331) account, or where a CLIENT lands mid-flow
// before a project is resolved (/client/onboarding, on a 404 from
// GET /client/my-project) — must be excluded, or landing here re-enters the
// same check on every render (§7.13 opt-out pattern). Exported so the test
// suite can assert this set is exactly these routes, not just presence-test
// a few of them (§7.13's stated standard).
export const CLIENT_LANDING_ROUTES = new Set([
  '/client/dashboard',
  '/client/select-project',
  '/client/onboarding',
  '/member/dashboard',
]);

// R1 (#2282) — CLIENT-role stakeholders have legitimate, already-linked
// destinations inside /projects/:projectId/* (DEF-13's Open Questions link,
// ClientDashboard.tsx:242; the Change-Orders review link, :637) — access
// itself is enforced server-side (assertArtefactProjectOrTenantAccess). A
// literal path Set can't express "any subpath of a project this account
// already holds", and the first version of this fix bounced both links
// straight back to /client/dashboard. Matched separately below, against the
// CLIENT's own /client/my-projects result, not a fixed list.
const PROJECT_ROUTE = /^\/projects\/([^/]+)/;

/** #3248 — /login, carrying `here` as ?redirect= only when isSafeRedirect accepts it. */
export function loginPathFor(here: string): string {
  return isSafeRedirect(here) ? `/login?redirect=${encodeURIComponent(here)}` : '/login';
}

export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const token = localStorage.getItem('blueprint_token');
  const location = useLocation();

  // Synchronous and cheap: a present tenantId claim is every role OTHER
  // than CLIENT-only (resolveNonClientDefaultTenantId, #1666/#1608), so this
  // is false for the overwhelming majority of renders — no state, no
  // effect, no network call, byte-identical to pre-#2282 behavior for every
  // other role.
  const claims = token ? decodeJwtPayload<{ tenantId?: string }>(token) : null;
  const needsClientCheck = !!token && !claims?.tenantId && !CLIENT_LANDING_ROUTES.has(location.pathname);
  const requestedProjectId = location.pathname.match(PROJECT_ROUTE)?.[1] ?? null;

  // undefined = check in flight (CLIENT-only account only); null = checked,
  // stay on the originally-requested route; string = redirect target.
  const [redirectTo, setRedirectTo] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!needsClientCheck) return;
    let cancelled = false;
    apiFetch<{ projects: Array<{ projectId: string }> }>('/client/my-projects')
      .then(({ projects }) => {
        if (cancelled) return;
        // Already on a project this CLIENT holds active stakeholder access
        // to — e.g. the Open Questions / Change-Orders links above. Nothing
        // to redirect; let the requested page render (server enforces the
        // rest).
        if (requestedProjectId && projects.some(p => p.projectId === requestedProjectId)) {
          setRedirectTo(null);
          return;
        }
        if (projects.length === 1) {
          setRedirectTo(`/client/dashboard?projectId=${projects[0].projectId}`);
        } else if (projects.length > 1) {
          // #2220 — no unique constraint on project_stakeholders(userId,
          // projectId); let the picker's own tested logic decide, don't
          // guess here.
          setRedirectTo('/client/select-project');
        } else {
          // Zero project_stakeholders rows. NOT necessarily "no access at
          // all" — a project_members-only account (#2331, no
          // tenant_members row either) also has no tenantId claim and also
          // lands here, and belongs on /member/dashboard, not on
          // ClientProjectPicker's "No project assigned" message. Stay on
          // the originally-requested route rather than assert a message
          // that would be wrong for that account shape; see PR description
          // for the follow-on this leaves open.
          setRedirectTo(null);
        }
      })
      .catch(() => { if (!cancelled) setRedirectTo(null); });
    return () => { cancelled = true; };
  }, [needsClientCheck, token, location.pathname, requestedProjectId]);

  // #3248 (recon §5.4) — keep the deep link through sign-in when it is a safe,
  // same-origin path; LoginPage honours ?redirect= after login.
  if (!token) return <Navigate to={loginPathFor(location.pathname + location.search)} replace />;
  if (needsClientCheck) {
    if (redirectTo === undefined) return null;
    if (redirectTo) return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}
